// src/components/SelectorCotizacionModal.jsx
import { useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { getSubCollection } from "../utils/getSubCollection";
import { createPortal } from "react-dom";

export default function SelectorCotizacionModal({
  ownerUid,
  onClose,
  onSelect,
  preselectedIds = [],
}) {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [search, setSearch] = useState("");
  const [hoverId, setHoverId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(preselectedIds);

  useEffect(() => {
    if (!ownerUid) return;
    const colRef = getSubCollection(ownerUid, "cotizaciones");
    const unsub = onSnapshot(colRef, (snap) => {
      setCotizaciones(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [ownerUid]);

  // si cambian las preseleccionadas (edición / re-abrir modal)
  useEffect(() => {
    setSelectedIds(preselectedIds || []);
  }, [preselectedIds]);

  const filtradas = cotizaciones.filter((c) => {
    const txt = `${c.numeroCotizacion || ""} ${c.nombreCliente || ""} ${
      c.referencia || ""
    } ${c.total || ""}`
      .toString()
      .toLowerCase();
    return txt.includes(search.toLowerCase());
  });

  // 🔵 multi-select: toggle de ids
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.45)",
      backdropFilter: "blur(2px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      zIndex: 3000,
    },
    card: {
      width: "100%",
      maxWidth: 920,
      maxHeight: "88vh",
      background: "#ffffff",
      borderRadius: 16,
      boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      fontFamily: "Inter, sans-serif",
    },
    header: {
      padding: "18px 24px 12px",
      borderBottom: "1px solid #E5E9F5",
    },
    headerTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 4,
      gap: 12,
    },
    title: {
      margin: 0,
      fontSize: 18,
      fontWeight: 600,
      color: "#0f172a",
    },
    subtitle: {
      margin: 0,
      fontSize: 13,
      color: "#64748b",
    },
    closeBtn: {
      border: "none",
      background: "transparent",
      padding: 4,
      borderRadius: 999,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 16,
      color: "#64748b",
    },
    body: {
      padding: "12px 24px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      overflow: "hidden",
    },
    searchBoxWrap: {
      margin: "4px 0 8px",
    },
    searchBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 999,
      border: "1px solid #E2E8F0",
      background: "#F8FAFC",
      fontSize: 14,
    },
    searchIcon: {
      fontSize: 15,
      color: "#94A3B8",
    },
    searchInput: {
      border: "none",
      outline: "none",
      background: "transparent",
      flex: 1,
      fontSize: 14,
      color: "#0f172a",
    },
    tableWrapper: {
      borderRadius: 12,
      border: "1px solid #E5E9F5",
      overflow: "hidden",
      background: "#fff",
      display: "flex",
      flexDirection: "column",
      minHeight: 160,
    },
    headRow: {
      display: "grid",
      gridTemplateColumns:
        "minmax(130px, 1.1fr) minmax(180px, 1.6fr) minmax(200px, 2fr) minmax(130px, 1.1fr) minmax(140px, 1.1fr)",
      padding: "10px 16px",
      fontSize: 12,
      fontWeight: 600,
      color: "#64748B",
      background: "#F8FAFF",
      borderBottom: "1px solid #E5E9F5",
    },
    headCell: {
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      textAlign: "center",
    },
    lista: {
      maxHeight: 360,
      overflowY: "auto",
    },
    row: {
      display: "grid",
      gridTemplateColumns:
        "minmax(130px, 1.1fr) minmax(180px, 1.6fr) minmax(200px, 2fr) minmax(130px, 1.1fr) minmax(140px, 1.1fr)",
      alignItems: "center",
      border: "none",
      width: "100%",
      padding: "10px 16px",
      background: "#ffffff",
      cursor: "pointer",
      textAlign: "left",
      fontSize: 13,
      color: "#0f172a",
      borderBottom: "1px solid #F1F5F9",
      boxSizing: "border-box",
      transition: "background 0.15s ease",
    },
    rowHover: {
      background: "#F4F7FF",
    },
    rowSelected: {
      background: "#E0ECFF",
      borderLeft: "3px solid #2563EB",
    },
    codigoLink: {
      color: "#2563EB",
      textDecoration: "underline",
      textDecorationThickness: "1px",
      textUnderlineOffset: "2px",
      fontWeight: 500,
    },
    cliente: { fontWeight: 500 },
    referencia: { color: "#64748B" },
    monto: { fontWeight: 500, textAlign: "right" },
    fecha: {
      color: "#64748B",
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
    empty: {
      padding: "18px 20px",
      fontSize: 14,
      color: "#94A3B8",
      textAlign: "center",
    },
    footer: {
      padding: "10px 24px 14px",
      borderTop: "1px solid #E5E9F5",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 10,
      fontSize: 13,
      background: "#F9FAFB",
    },
    footerText: {
      marginRight: "auto",
      color: "#94A3B8",
    },
    selectedChip: {
      marginRight: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      background: "#E0ECFF",
      color: "#1D4ED8",
      fontSize: 12,
      fontWeight: 500,
    },
    btnGhost: {
      padding: "8px 14px",
      borderRadius: 999,
      border: "1px solid transparent",
      background: "transparent",
      fontSize: 13,
      cursor: "pointer",
      color: "#475569",
    },
    btnPrimary: {
      padding: "8px 18px",
      borderRadius: 999,
      border: "none",
      fontSize: 13,
      cursor: "pointer",
      background: "#2563EB",
      color: "#ffffff",
      fontWeight: 500,
    },
    btnPrimaryDisabled: {
      opacity: 0.55,
      cursor: "not-allowed",
    },
  };

  const selectedItems =
    selectedIds.length > 0
      ? cotizaciones.filter((c) => selectedIds.includes(c.id))
      : [];

  const handleGuardar = () => {
    if (!selectedItems.length) return;
    onSelect(selectedItems); // 👈 devolvemos TODAS las seleccionadas
    onClose();
  };

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <h3 style={styles.title}>Relacionar cotizaciones</h3>
            <button
              type="button"
              onClick={onClose}
              style={styles.closeBtn}
              title="Cerrar"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <p style={styles.subtitle}>
            Busca una cotización para relacionarla con esta remisión.
          </p>
        </div>

        {/* BODY */}
        <div style={styles.body}>
          {/* Buscador */}
          <div style={styles.searchBoxWrap}>
            <div style={styles.searchBox}>
              <i className="bi bi-search" style={styles.searchIcon} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Buscar por código, cliente, referencia o monto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Tabla */}
          <div style={styles.tableWrapper}>
            {/* Encabezados */}
            <div style={styles.headRow}>
              <div style={styles.headCell}>Código</div>
              <div style={styles.headCell}>Cliente</div>
              <div style={styles.headCell}>Referencia</div>
              <div style={styles.headCell}>Monto</div>
              <div style={styles.headCell}>Fecha</div>
            </div>

            {/* Filas */}
            <div style={styles.lista}>
              {filtradas.map((c) => {
                const isHover = hoverId === c.id;
                const isSelected = selectedIds.includes(c.id);
                const fechaStr = c.fechaCotizacion
                  ? new Date(c.fechaCotizacion).toISOString().slice(0, 10)
                  : "";

                return (
                  <div
                    key={c.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSelect(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSelect(c.id);
                      }
                    }}
                    style={{
                      ...styles.row,
                      ...(isHover ? styles.rowHover : {}),
                      ...(isSelected ? styles.rowSelected : {}),
                    }}
                    onMouseEnter={() => setHoverId(c.id)}
                    onMouseLeave={() => setHoverId(null)}
                  >
                    <span style={styles.codigoLink}>
                      {c.numeroCotizacion || "—"}
                    </span>
                    <span style={styles.cliente}>
                      {c.nombreCliente || "Sin cliente"}
                    </span>
                    <span style={styles.referencia}>
                      {c.referencia || "Sin referencia"}
                    </span>
                    <span style={styles.monto}>
                      {(c.divisa || "MXN") +
                        " " +
                        Number(c.total || 0).toLocaleString("es-MX", {
                          minimumFractionDigits: 2,
                        })}
                    </span>
                    <span style={styles.fecha}>{fechaStr}</span>
                  </div>
                );
              })}

              {filtradas.length === 0 && (
                <div style={styles.empty}>
                  No se encontraron cotizaciones con ese criterio de búsqueda.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          {selectedItems.length ? (
            <div style={styles.selectedChip}>
              <span>
                Seleccionada
                {selectedItems.length === 1 ? "" : "s"}:
              </span>
              <span>
                {selectedItems
                  .slice(0, 3)
                  .map(
                    (c) =>
                      `${c.numeroCotizacion} · ${
                        c.nombreCliente || "Sin cliente"
                      }`
                  )
                  .join(" , ")}
                {selectedItems.length > 3 &&
                  `  +${selectedItems.length - 3} más`}
              </span>
            </div>
          ) : (
            <span style={styles.footerText}>
              {filtradas.length} cotización
              {filtradas.length === 1 ? "" : "es"} encontradas · Ninguna
              seleccionada
            </span>
          )}

          <button type="button" style={styles.btnGhost} onClick={onClose}>
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleGuardar}
            disabled={!selectedItems.length}
            style={{
              ...styles.btnPrimary,
              ...(!selectedItems.length ? styles.btnPrimaryDisabled : {}),
            }}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
